import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Crown, ShieldCheck } from 'lucide-react';
import { tierLabel } from '../../config/labels';
import { usePrivacyMode } from '../../contexts/PrivacyModeContext';

/**
 * MembershipBadgeCard — replaces the tier badge that used to sit in the
 * dashboard header. Same visual weight (Crown + coloured pill), but now
 * lives inside Settings where only the member sees it.
 *
 * Hidden while Privacy Mode is ON so a shoulder-surfer in Settings
 * can't see the tier either.
 */
const MembershipBadgeCard = ({ user, onUpgradeClick }) => {
  const { privacyMode } = usePrivacyMode();
  if (!user || privacyMode) return null;
  const display = tierLabel(user.tier);
  const isClarity = user.tier === 'premium_portfolio' || user.tier === 'premium_continuity';
  const palette = isClarity
    ? { chip: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: 'text-emerald-700', line: 'Top tier — full clarity + all analytics.' }
    : { chip: 'bg-blue-100 text-blue-800 border-blue-200', icon: 'text-blue-700', line: 'Gateway tier — Continuity System + trusted contacts.' };

  return (
    <Card data-testid="membership-badge-card" className="border-slate-200">
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-50 flex items-center justify-center border border-slate-200">
              <ShieldCheck className={`w-5 h-5 ${palette.icon}`} aria-hidden="true" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Your Membership</p>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge
                  data-testid="settings-tier-badge"
                  className={`flex items-center gap-1.5 px-2.5 py-1 text-sm font-semibold border ${palette.chip}`}
                >
                  <Crown className="w-3.5 h-3.5" />
                  <span>{display}</span>
                </Badge>
              </div>
              <p className="text-xs text-slate-500 mt-1">{palette.line}</p>
            </div>
          </div>
          {onUpgradeClick && user.tier === 'portfolio_continuity' && (
            <button
              type="button"
              onClick={onUpgradeClick}
              data-testid="settings-tier-upgrade-link"
              className="text-sm text-emerald-700 hover:text-emerald-900 underline-offset-4 hover:underline font-medium"
            >
              Upgrade to Portfolio Clarity &rarr;
            </button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MembershipBadgeCard;
